import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  LogOut, ChevronLeft, Inbox, ClipboardList, Award, DollarSign,
  ShieldCheck, BarChart3, User, AlertTriangle, CheckCircle2, Clock, Ban,
} from 'lucide-react';
import {
  portalFetch, portalPost, portalPut, portalLogout, getActivePortal,
} from '../lib/portalAuth';

const f$ = (n) => n === null || n === undefined ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const CONF_STYLE = {
  AWARD_PENDING_CONFIRMATION: 'bg-amber-100 text-amber-800',
  CONFIRMED: 'bg-emerald-100 text-emerald-800',
  CONFIRMATION_OVERDUE: 'bg-red-100 text-red-800',
  FAILED_CONFIRMATION: 'bg-red-100 text-red-800',
  CANCELLED_BY_PROVIDER: 'bg-gray-200 text-gray-700',
};
const PAYOUT_STYLE = {
  NOT_READY: 'bg-gray-100 text-gray-600', READY: 'bg-blue-100 text-blue-800',
  HOLD_REVIEW: 'bg-orange-100 text-orange-800', APPROVED: 'bg-indigo-100 text-indigo-800',
  SCHEDULED: 'bg-violet-100 text-violet-800', PAID: 'bg-emerald-100 text-emerald-800',
  DISPUTED: 'bg-red-100 text-red-800',
};

const TABS = [
  { id: 'available', label: 'Available', icon: Inbox },
  { id: 'offers', label: 'My Offers', icon: ClipboardList },
  { id: 'awarded', label: 'Awarded & Service', icon: Award },
  { id: 'payouts', label: 'Payouts', icon: DollarSign },
  { id: 'compliance', label: 'Compliance', icon: ShieldCheck },
  { id: 'performance', label: 'Performance', icon: BarChart3 },
  { id: 'profile', label: 'Profile', icon: User },
];

export default function PartnerPortal() {
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('available');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [err, setErr] = useState('');

  useEffect(() => {
    const p = getActivePortal();
    if (!p) { navigate('/broker/login'); return; }
    setPortal(p);
    portalFetch(p, '/api/collective/partner/me')
      .then((m) => { setMe(m); setLoading(false); })
      .catch((e) => {
        setLoading(false);
        if ((e.message || '').toLowerCase().includes('provider')) setErr('This account is not linked to a Driven Collective provider identity.');
        else navigate('/broker/login');
      });
  }, [navigate]);

  const load = useCallback(async (t) => {
    if (!portal) return;
    const map = {
      available: '/api/collective/partner/available-requests',
      offers: '/api/collective/partner/offers',
      awarded: '/api/collective/partner/awarded',
      payouts: '/api/collective/partner/payouts',
      compliance: '/api/collective/partner/compliance',
      performance: '/api/collective/partner/performance',
    };
    if (!map[t]) return;
    try {
      const res = await portalFetch(portal, map[t]);
      setData((d) => ({ ...d, [t]: res }));
    } catch (e) { setErr(e.message); }
  }, [portal]);

  useEffect(() => { if (portal && me) load(tab); }, [tab, portal, me, load]);

  const logout = async () => { await portalLogout(portal); navigate('/broker/login'); };

  if (loading) return <Center>Loading Driven Collective portal…</Center>;
  if (err && !me) return <Center><div className="text-center"><Ban className="h-8 w-8 text-red-500 mx-auto mb-3" /><p className="text-sm text-gray-600 max-w-sm">{err}</p><Link to="/" className="text-red-600 text-sm underline mt-3 inline-block">Back to ACE</Link></div></Center>;
  if (!me) return null;

  const primary = me.providers?.[0] || {};

  return (
    <div className="min-h-screen bg-stone-50" data-testid="partner-portal">
      {/* Header */}
      <div className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-5 py-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/" className="inline-flex items-center text-[10px] uppercase tracking-widest text-gray-400 hover:text-white mb-1">
              <ChevronLeft className="h-3 w-3 mr-1" /> Back to ACE
            </Link>
            <p className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">ACE · Driven Collective</p>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight mt-1" data-testid="partner-name">{primary.provider_name || 'Partner'}</h1>
            <p className="text-xs text-gray-400 mt-1">
              {primary.provider_id} · {humanize(primary.provider_type || '')}
              {primary.suspended && <span className="ml-2 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold">SUSPENDED</span>}
              {!primary.suspended && <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-800 text-gray-200 text-[10px] font-bold">{primary.current_tier}</span>}
            </p>
          </div>
          <Button onClick={logout} variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="partner-logout">
            <LogOut className="h-3 w-3 mr-1" /> Sign out
          </Button>
        </div>
        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex gap-1 overflow-x-auto pb-0">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? 'border-red-500 text-white' : 'border-transparent text-gray-400 hover:text-gray-200'}`}>
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-6 space-y-4">
        {tab === 'available' && <Available data={data.available} portal={portal} reload={() => { load('available'); load('offers'); }} />}
        {tab === 'offers' && <Offers data={data.offers} portal={portal} reload={() => { load('offers'); load('available'); }} />}
        {tab === 'awarded' && <Awarded data={data.awarded} portal={portal} reload={() => { load('awarded'); load('payouts'); }} />}
        {tab === 'payouts' && <Payouts data={data.payouts} portal={portal} reload={() => load('payouts')} />}
        {tab === 'compliance' && <Compliance data={data.compliance} me={me} portal={portal} reload={() => load('compliance')} />}
        {tab === 'performance' && <Performance data={data.performance} />}
        {tab === 'profile' && <Profile me={me} portal={portal} reloadMe={() => portalFetch(portal, '/api/collective/partner/me').then(setMe)} />}
      </div>
    </div>
  );
}

// ─── AVAILABLE REQUESTS ───────────────────────────────────────────────────────
const Available = ({ data, portal, reload }) => {
  if (!data) return <Loading />;
  if (data.note) return <Empty icon={Ban} text={data.note} />;
  if (!data.requests?.length) return <Empty icon={Inbox} text="No available requests right now. Eligible fulfillment requests will appear here." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="available-list">
      {data.requests.map((r) => <RequestCard key={r.offer_id} r={r} portal={portal} reload={reload} />)}
    </div>
  );
};

const RequestCard = ({ r, portal, reload }) => {
  const [mode, setMode] = useState(null);
  const [cost, setCost] = useState('');
  const [asset, setAsset] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (status) => {
    setBusy(true);
    try {
      await portalPost(portal, `/api/collective/partner/offers/${r.offer_id}/respond`, {
        status, ...(cost ? { proposed_cost: parseFloat(cost) } : {}), ...(asset ? { asset_details: asset } : {}),
      });
      reload();
    } catch (e) { alert(e.message); } finally { setBusy(false); setMode(null); }
  };
  return (
    <Card className="p-5" data-testid={`request-${r.offer_id}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold">{humanize(r.requirement_type)}</p>
          <h3 className="font-bold text-lg mt-0.5">{r.market || 'Market TBD'}</h3>
        </div>
        <span className="text-[10px] text-gray-400 font-mono">{r.offer_id}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
        <Field label="Date" value={r.service_date || 'TBD'} />
        <Field label="Window" value={r.service_window || '—'} />
        <Field label="Passengers" value={r.passenger_capacity ?? '—'} />
        <Field label="Asset req." value={r.vehicle_or_asset_requirement || '—'} />
      </div>
      {r.special_requirements && <p className="text-xs text-gray-600 mt-2 bg-stone-100 rounded p-2">{r.special_requirements}</p>}
      {r.response_deadline && <p className="text-[11px] text-gray-500 mt-2 flex items-center gap-1"><Clock className="h-3 w-3" /> Respond by {r.response_deadline}</p>}
      {mode === 'counter' && (
        <div className="mt-3 space-y-2 bg-stone-50 border rounded p-3">
          <input data-testid={`counter-cost-${r.offer_id}`} value={cost} onChange={e => setCost(e.target.value)} placeholder="Your cost (USD)" className="w-full text-sm border rounded px-2 py-1.5" />
          <input data-testid={`counter-asset-${r.offer_id}`} value={asset} onChange={e => setAsset(e.target.value)} placeholder="Vehicle / asset" className="w-full text-sm border rounded px-2 py-1.5" />
          <Button size="sm" disabled={busy} onClick={() => act('COUNTERED')} data-testid={`submit-counter-${r.offer_id}`} className="w-full bg-black text-white">Send counter</Button>
        </div>
      )}
      <div className="flex gap-2 mt-3">
        <Button size="sm" disabled={busy} onClick={() => act('ACCEPTED')} data-testid={`accept-${r.offer_id}`} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">Accept</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode(mode === 'counter' ? null : 'counter')} data-testid={`counter-${r.offer_id}`} className="flex-1">Counter</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => act('DECLINED')} data-testid={`decline-${r.offer_id}`} className="flex-1 text-red-600 border-red-200">Decline</Button>
      </div>
    </Card>
  );
};

// ─── MY OFFERS ────────────────────────────────────────────────────────────────
const Offers = ({ data }) => {
  if (!data) return <Loading />;
  if (!data.offers?.length) return <Empty icon={ClipboardList} text="No offers yet." />;
  return (
    <Card className="p-0 overflow-hidden" data-testid="offers-list">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
          <tr><th className="text-left px-4 py-2.5">Type</th><th className="text-left px-4 py-2.5">Market</th><th className="text-left px-4 py-2.5">Date</th><th className="text-center px-4 py-2.5">Status</th></tr>
        </thead>
        <tbody>
          {data.offers.map((o) => (
            <tr key={o.offer_id} className="border-t" data-testid={`offer-row-${o.offer_id}`}>
              <td className="px-4 py-2.5">{humanize(o.requirement_type)}</td>
              <td className="px-4 py-2.5">{o.market || '—'}</td>
              <td className="px-4 py-2.5">{o.service_date || '—'}</td>
              <td className="px-4 py-2.5 text-center"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-700">{o.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};

// ─── AWARDED & SERVICE ──────────────────────────────────────────────────────────
const Awarded = ({ data, portal, reload }) => {
  if (!data) return <Loading />;
  if (!data.AWARDED?.length) return <Empty icon={Award} text="No awarded work yet." />;
  return (
    <div className="space-y-4" data-testid="awarded-list">
      {data.AWARDED.map((a) => <AwardCard key={a.offer_id} a={a} portal={portal} reload={reload} />)}
    </div>
  );
};

const AwardCard = ({ a, portal, reload }) => {
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(false);
  const [driver, setDriver] = useState('');
  const [note, setNote] = useState('');
  const [incident, setIncident] = useState(false);
  const call = async (path, body) => {
    setBusy(true);
    try { await portalPost(portal, path, body); reload(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const base = `/api/collective/partner/awards/${a.offer_id}`;
  return (
    <Card className="p-5" data-testid={`award-${a.offer_id}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold">{humanize(a.requirement_type)}</p>
          <h3 className="font-bold text-lg mt-0.5">{a.market} · {a.service_date || 'TBD'}</h3>
          <p className="text-xs text-gray-500 mt-0.5 font-mono">{a.award_id}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${CONF_STYLE[a.confirmation_status]}`} data-testid={`conf-${a.offer_id}`}>{humanize(a.confirmation_status)}</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700">{humanize(a.service_state)}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-sm">
        <Field label="Awarded amount" value={f$(a.awarded_amount)} />
        <Field label="Asset" value={a.assigned_asset || '—'} />
        <Field label="Passengers" value={a.passenger_capacity ?? '—'} />
        <Field label="Payout" value={a.payout_state ? humanize(a.payout_state) : '—'} />
      </div>

      {a.confirmation_status !== 'CONFIRMED' && a.service_state !== 'COMPLETED' && (
        <div className="mt-3 bg-stone-50 border rounded p-3 space-y-2">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} data-testid={`ack-${a.offer_id}`} /> I acknowledge ACE service standards</label>
          <input value={driver} onChange={e => setDriver(e.target.value)} placeholder="Assigned driver / operator" className="w-full text-sm border rounded px-2 py-1.5" data-testid={`driver-${a.offer_id}`} />
          <Button size="sm" disabled={busy || !ack} onClick={() => call(`${base}/confirm`, { service_standard_ack: ack, driver_operator: driver })} data-testid={`confirm-${a.offer_id}`} className="w-full bg-black text-white">Confirm award</Button>
        </div>
      )}

      {a.confirmation_status === 'CONFIRMED' && a.service_state === 'CONFIRMED' && (
        <Button size="sm" disabled={busy} onClick={() => call(`${base}/service-state`, { service_state: 'IN_SERVICE' })} data-testid={`start-service-${a.offer_id}`} className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white">Start service (In Service)</Button>
      )}

      {a.service_state === 'IN_SERVICE' && !a.completion_submitted && (
        <div className="mt-3 bg-stone-50 border rounded p-3 space-y-2">
          <p className="text-xs font-bold text-gray-700">Submit completion evidence</p>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Completion note" className="w-full text-sm border rounded px-2 py-1.5" data-testid={`comp-note-${a.offer_id}`} />
          <label className="flex items-center gap-2 text-xs text-red-700"><input type="checkbox" checked={incident} onChange={e => setIncident(e.target.checked)} data-testid={`incident-${a.offer_id}`} /> Report an incident / issue</label>
          <Button size="sm" disabled={busy} onClick={() => call(`${base}/completion`, { completed: true, completion_note: note, incident })} data-testid={`complete-${a.offer_id}`} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">Submit completion</Button>
        </div>
      )}

      {a.service_state === 'COMPLETED' && (
        <p className="mt-3 text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Completed — payout {a.payout_state ? humanize(a.payout_state).toLowerCase() : 'pending ACE reconciliation'}.</p>
      )}
    </Card>
  );
};

// ─── PAYOUTS ────────────────────────────────────────────────────────────────
const Payouts = ({ data, portal, reload }) => {
  if (!data) return <Loading />;
  const dispute = async (recId) => {
    const reason = window.prompt('Reason for disputing this payout?');
    if (!reason) return;
    try { await portalPost(portal, `/api/collective/partner/payouts/${recId}/dispute`, { reason }); reload(); }
    catch (e) { alert(e.message); }
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3" data-testid="payout-totals">
        <Kpi label="Approved" value={f$(data.totals?.approved)} color="indigo" />
        <Kpi label="Scheduled" value={f$(data.totals?.scheduled)} color="violet" />
        <Kpi label="Paid" value={f$(data.totals?.paid)} color="emerald" />
      </div>
      {!data.payouts?.length ? <Empty icon={DollarSign} text="No payouts yet." /> : (
        <Card className="p-0 overflow-hidden" data-testid="payouts-list">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr><th className="text-left px-4 py-2.5">Award</th><th className="text-right px-4 py-2.5">Awarded</th><th className="text-right px-4 py-2.5">Adjust</th><th className="text-right px-4 py-2.5">Final</th><th className="text-center px-4 py-2.5">State</th><th className="text-center px-4 py-2.5"></th></tr>
            </thead>
            <tbody>
              {data.payouts.map((p) => (
                <tr key={p.reconciliation_id} className="border-t" data-testid={`payout-row-${p.reconciliation_id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{p.fulfillment_award_id}</td>
                  <td className="px-4 py-2.5 text-right">{f$(p.awarded_amount)}</td>
                  <td className="px-4 py-2.5 text-right">{f$(p.approved_adjustments)}</td>
                  <td className="px-4 py-2.5 text-right font-bold">{f$(p.final_payout)}</td>
                  <td className="px-4 py-2.5 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${PAYOUT_STYLE[p.payout_state]}`}>{humanize(p.payout_state)}</span></td>
                  <td className="px-4 py-2.5 text-center">
                    {(p.payout_state === 'APPROVED' || p.payout_state === 'SCHEDULED') && (
                      <button onClick={() => dispute(p.reconciliation_id)} data-testid={`dispute-${p.reconciliation_id}`} className="text-[11px] text-red-600 underline">Dispute</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <p className="text-[11px] text-gray-400 text-center">ACE reviews and approves payouts. You cannot edit an approved payable — raise a dispute for review.</p>
    </div>
  );
};

// ─── COMPLIANCE ──────────────────────────────────────────────────────────────
const Compliance = ({ data, me, portal, reload }) => {
  const [type, setType] = useState('insurance');
  const [cap, setCap] = useState('');
  const [expiry, setExpiry] = useState('');
  const [busy, setBusy] = useState(false);
  if (!data) return <Loading />;
  const pid = me.providers?.[0]?.provider_id;
  const caps = me.providers?.[0]?.approved_capabilities || [];
  const add = async () => {
    setBusy(true);
    try {
      await portalPost(portal, '/api/collective/partner/compliance', {
        provider_id: pid, type, capability: cap || undefined, required: true, expiry_date: expiry,
      });
      setExpiry(''); setCap(''); reload();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const blocked = data.ineligible_capabilities || {};
  return (
    <div className="space-y-4">
      {Object.keys(blocked).length > 0 && (
        <Card className="p-4 border-orange-200 bg-orange-50" data-testid="compliance-blocked">
          <p className="text-xs font-bold text-orange-800 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Capabilities currently ineligible</p>
          {Object.entries(blocked).map(([p, cs]) => <p key={p} className="text-xs text-orange-700 mt-1">{p}: {cs.map(humanize).join(', ')}</p>)}
        </Card>
      )}
      <Card className="p-5">
        <h3 className="font-bold mb-3 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-red-600" /> Add compliance document</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <select value={type} onChange={e => setType(e.target.value)} className="text-sm border rounded px-2 py-1.5" data-testid="compliance-type">
            <option value="insurance">Insurance evidence</option>
            <option value="operating_authority">Operating authority</option>
            <option value="vehicle_document">Vehicle document</option>
            <option value="service_standard_ack">Service-standard ack</option>
            <option value="other">Other</option>
          </select>
          <select value={cap} onChange={e => setCap(e.target.value)} className="text-sm border rounded px-2 py-1.5" data-testid="compliance-capability">
            <option value="">All capabilities</option>
            {caps.map(c => <option key={c} value={c}>{humanize(c)}</option>)}
          </select>
          <input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} className="text-sm border rounded px-2 py-1.5" data-testid="compliance-expiry" />
          <Button size="sm" disabled={busy} onClick={add} data-testid="compliance-add" className="bg-black text-white">Submit for review</Button>
        </div>
      </Card>
      {!data.documents?.length ? <Empty icon={ShieldCheck} text="No compliance documents on file yet." /> : (
        <Card className="p-0 overflow-hidden" data-testid="compliance-list">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr><th className="text-left px-4 py-2.5">Type</th><th className="text-left px-4 py-2.5">Capability</th><th className="text-left px-4 py-2.5">Expiry</th><th className="text-center px-4 py-2.5">Status</th></tr>
            </thead>
            <tbody>
              {data.documents.map((d) => (
                <tr key={d.compliance_id} className="border-t" data-testid={`compliance-row-${d.compliance_id}`}>
                  <td className="px-4 py-2.5">{humanize(d.type)}</td>
                  <td className="px-4 py-2.5">{d.capability ? humanize(d.capability) : 'All'}</td>
                  <td className="px-4 py-2.5">{d.expiry_date || '—'}</td>
                  <td className="px-4 py-2.5 text-center"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-700">{humanize(d.effective_status)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

// ─── PERFORMANCE ─────────────────────────────────────────────────────────────
const Performance = ({ data }) => {
  if (!data) return <Loading />;
  const entries = Object.entries(data.performance || {});
  if (!entries.length) return <Empty icon={BarChart3} text="No performance data yet." />;
  const pct = (m) => !m || m.value === null || m.value === undefined ? '—' : `${Math.round(m.value * 100)}%`;
  const val = (m) => m?.value ?? null;
  const n = (m) => m?.sample_size ?? 0;
  return (
    <div className="space-y-4" data-testid="performance">
      {entries.map(([pid, p]) => (
        <Card key={pid} className="p-5">
          <p className="text-xs font-bold text-gray-500 mb-3">{pid} · trailing {p.window_days} days</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Requests" value={val(p.requests_received)} sample={n(p.requests_received)} />
            <Metric label="Response rate" value={pct(p.response_rate)} sample={n(p.response_rate)} />
            <Metric label="Acceptance" value={pct(p.acceptance_rate)} sample={n(p.acceptance_rate)} />
            <Metric label="Avg response" value={val(p.avg_response_hours) !== null ? `${val(p.avg_response_hours)}h` : '—'} sample={n(p.avg_response_hours)} />
            <Metric label="Confirmation" value={pct(p.confirmation_rate)} sample={n(p.confirmation_rate)} />
            <Metric label="Completion" value={pct(p.completion_rate)} sample={n(p.completion_rate)} />
            <Metric label="Cancellation" value={pct(p.cancellation_rate)} sample={n(p.cancellation_rate)} />
            <Metric label="Disputes" value={pct(p.dispute_rate)} sample={n(p.dispute_rate)} />
          </div>
          <p className="text-[11px] text-gray-400 mt-3">Every metric shows its sample size. Tiers are set by ACE — metrics inform review, not automatic changes.</p>
        </Card>
      ))}
    </div>
  );
};

// ─── PROFILE ─────────────────────────────────────────────────────────────────
const Profile = ({ me, portal, reloadMe }) => {
  const p = me.providers?.[0] || {};
  const [capacity, setCapacity] = useState(p.capacity_status || 'unknown');
  const [reqCap, setReqCap] = useState('');
  const [busy, setBusy] = useState(false);
  const ALL_CAPS = ['CONCIERGE_DRIVEN', 'GROUP_DRIVEN', 'EXOTIC_DRIVEN', 'VEHICLE_ACCESS_RENTAL', 'PRODUCTION_VEHICLE'];
  const save = async (body) => {
    setBusy(true);
    try { await portalPut(portal, '/api/collective/partner/profile', { provider_id: p.provider_id, ...body }); await reloadMe(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="profile">
      <Card className="p-5">
        <h3 className="font-bold mb-3">Approved capabilities</h3>
        <div className="flex flex-wrap gap-2">
          {(p.approved_capabilities || []).length ? p.approved_capabilities.map(c => (
            <span key={c} className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">{humanize(c)}</span>
          )) : <span className="text-xs text-gray-400 italic">None approved yet.</span>}
          {(p.pending_capabilities || []).map(c => (
            <span key={c} className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">{humanize(c)} (pending)</span>
          ))}
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="font-bold mb-3">Availability</h3>
        <div className="flex items-center gap-2">
          <select value={capacity} onChange={e => setCapacity(e.target.value)} className="text-sm border rounded px-2 py-1.5" data-testid="capacity-select">
            <option value="available">Available</option>
            <option value="limited">Limited</option>
            <option value="unavailable">Unavailable</option>
            <option value="unknown">Unknown</option>
          </select>
          <Button size="sm" disabled={busy} onClick={() => save({ capacity_status: capacity })} data-testid="save-capacity" className="bg-black text-white">Update</Button>
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="font-bold mb-3">Request a new capability</h3>
        <p className="text-xs text-gray-500 mb-2">New capabilities require ACE review before you can be awarded that work.</p>
        <div className="flex items-center gap-2">
          <select value={reqCap} onChange={e => setReqCap(e.target.value)} className="text-sm border rounded px-2 py-1.5" data-testid="request-cap-select">
            <option value="">Select capability…</option>
            {ALL_CAPS.map(c => <option key={c} value={c}>{humanize(c)}</option>)}
          </select>
          <Button size="sm" disabled={busy || !reqCap} onClick={() => save({ requested_capabilities: [reqCap] })} data-testid="request-cap" className="bg-black text-white">Request review</Button>
        </div>
      </Card>
    </div>
  );
};

// ─── shared bits ─────────────────────────────────────────────────────────────
const Field = ({ label, value }) => (
  <div><p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{label}</p><p className="text-sm font-semibold text-gray-900">{value}</p></div>
);
const Metric = ({ label, value, sample }) => (
  <div className="bg-stone-50 border rounded p-3"><p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{label}</p><p className="text-xl font-black mt-0.5">{value}</p><p className="text-[10px] text-gray-400">n={sample}</p></div>
);
const Kpi = ({ label, value, color }) => {
  const map = { indigo: 'border-indigo-200 text-indigo-700', violet: 'border-violet-200 text-violet-700', emerald: 'border-emerald-200 text-emerald-700' };
  return <Card className={`p-4 border ${map[color]}`}><p className="text-[10px] font-bold uppercase tracking-widest opacity-70">{label}</p><p className="text-2xl font-black mt-1">{value}</p></Card>;
};
const Empty = ({ icon: Icon, text }) => (
  <div className="text-center py-16" data-testid="empty-state"><Icon className="h-8 w-8 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-400 max-w-sm mx-auto">{text}</p></div>
);
const Loading = () => <div className="text-center py-16 text-sm text-gray-400">Loading…</div>;
const Center = ({ children }) => <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">{children}</div>;
